import type { UpdateMutationResult } from "@/customTypings/api/UpdateMutation";
import type {
    PhysicalStateCreatePayload,
    PhysicalStateUpdatePayload,
    IPhysicalStateDetail,
} from "@/customTypings/reference/PhysicalState";
import { PHYSICAL_STATE_CREATE_CONFLICT } from "@/customTypings/reference/PhysicalState";
import type { ReferenceRestoreDeletedPayload } from "@/utils/reference/referenceCreateConflict";
import { authHeader, handleResponse } from "../../utils";
import { appendIncludeAuditQuery, type DetailGetByIdOptions } from "@/utils/api/detailGetOptions";
import { postReferenceCreate } from "@/utils/reference/referenceCreateConflict";

export const physicalStatesService = {
    getById,
    create,
    restoreDeletedWithCreateValues,
    update,
    disable,
    enable,
    softDelete,
    restore,
};

function getById(id: string, options?: DetailGetByIdOptions) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        appendIncludeAuditQuery(`/api/referencedata/physical-states/${encodeURIComponent(id)}`, options),
        requestOptions
    ).then(handleResponse) as Promise<IPhysicalStateDetail>;
}

function create(payload: PhysicalStateCreatePayload) {
    return postReferenceCreate<IPhysicalStateDetail>(
        `/api/referencedata/physical-states/create`,
        payload,
        PHYSICAL_STATE_CREATE_CONFLICT.deletedRestoreAvailable
    );
}

function restoreDeletedWithCreateValues(payload: ReferenceRestoreDeletedPayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`/api/referencedata/physical-states/restore-deleted-with-create-values`, requestOptions).then(
        handleResponse
    ) as Promise<IPhysicalStateDetail>;
}

function update(payload: PhysicalStateUpdatePayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`/api/referencedata/physical-states/update`, requestOptions).then(
        handleResponse
    ) as Promise<UpdateMutationResult<IPhysicalStateDetail>>;
}

function disable(id: string) {
    return postQuery("disable", id);
}

function enable(id: string) {
    return postQuery("enable", id);
}

function softDelete(id: string) {
    return postQuery("softdelete", id);
}

function restore(id: string) {
    return postQuery("restore", id);
}

function postQuery(action: string, id: string) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        `/api/referencedata/physical-states/${action}?id=${encodeURIComponent(id)}`,
        requestOptions
    ).then(handleResponse);
}
